import { FunctionComponent, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { deleteProject, deleteProjectMaster } from "../../../Services/contacts";
import { showSnackbarAction } from "../../../Redux/actions";
import { themeColors } from "../../theme";
import "../../../theme-vars.css";
import { Box, Grid, useMediaQuery, useTheme } from "@mui/material";
import Modal, { Styles } from "react-modal";
import StyledCancelButton from "../../Buttons/StyledCancelButton";
import { Circles } from "react-loader-spinner";

// -------------------------------------------------------------
// Props Interface
// -------------------------------------------------------------
type props = {
    open: boolean;
    close: () => void;
    data: any;
    totalData: any;
    clearSelectedRowsData?: () => void;
    theme: any;
};

// -------------------------------------------------------------
// DeleteMasterProjectSelectedModal Component
// -------------------------------------------------------------
const DeleteMasterProjectSelectedModal: FunctionComponent<props> = ({
    open,
    close,
    clearSelectedRowsData,
    data,
    totalData,
    theme
}) => {
    // MUI theme + responsiveness handling
    const themeMUI = useTheme();
    const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));

    // Loader state
    const [load, setLoad] = useState(false);

    // Redux dispatcher
    const dispatcher = useDispatch();


    // -------------------------------------------------------------
    // Delete Selected Projects (grouped by organization)
    // -------------------------------------------------------------
    const deleteProjectFirebase = () => {
        if (!data || data.length === 0) return;

        setLoad(true);

        // STEP 1 → Group selected rows by organization
        const grouped = data.reduce((acc: any, curr: any) => {
            if (!acc[curr.organization_id]) acc[curr.organization_id] = [];
            acc[curr.organization_id].push(curr);
            return acc;
        }, {});

        // STEP 2 → For each organization, delete its selected projects
        Object.keys(grouped).forEach(async (orgId) => {
            const selectedProjects = grouped[orgId];

            // Filter ONLY Pre Launch projects
            const finalProjects = selectedProjects.filter(
                (x: any) => x.project_status === "Pre Launch"
            );

            if (finalProjects.length === 0) return;

            // Call Master delete function
            await deleteProjectMaster(
                orgId,
                finalProjects,
                dispatcher,
                (v: any) => setLoad(v)
            );
        });

        // STEP 3 → UI Cleanup
        close();
        setLoad(false);
        dispatcher(showSnackbarAction("Record Deleted", "success"));
        clearSelectedRowsData && clearSelectedRowsData();
        window.location.reload();
    };


    // -------------------------------------------------------------
    // Modal styling configuration
    // -------------------------------------------------------------
    const modalStyle: Styles = {
        overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        },
        content: {
            background: theme
                ? themeColors.cardBackgroundColor
                : themeColors.cardBackgroundColorDark,
            color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
            borderRadius: 16,
            padding: isMobile ? "24px 12px" : "40px 32px",
            maxWidth: 380,
            minWidth: 320,
            border: "none",
            boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            inset: "auto",
            margin: "auto",
        },
    };


    // -------------------------------------------------------------
    // RENDER UI
    // -------------------------------------------------------------
    return (
        <>
            <Modal
                isOpen={open}
                onRequestClose={close}
                shouldCloseOnOverlayClick={true}
                shouldCloseOnEsc={true}
                style={modalStyle}
                ariaHideApp={false}
            >
                <Box
                    sx={{
                        width: "100%",
                        maxWidth: 340,
                        mx: "auto",
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    <form style={{ width: "100%" }}>
                        {/* ----------------- Modal Title ----------------- */}
                        <Box sx={{ mb: 2 }}>
                            <h3
                                style={{
                                    color: theme ? "#272944" : "#fff",
                                    fontFamily: "var(--font-family-main)",
                                    fontSize: "var(--font-size-large)",
                                    marginBottom: 24,
                                    textAlign: "center",
                                    letterSpacing: 0.5,
                                }}
                            >
                                Are you sure you want to delete the records?
                            </h3>
                        </Box>

                        {/* ----------------- Displaying Selected Project List ----------------- */}
                        <Grid container spacing={2} sx={{ width: "100%", mt: 2, maxHeight: 200, overflowY: "auto" }}>
                            <ol>
                                {data
                                    .filter((i: any) => i.project_status === "Pre Launch")
                                    ?.map((dat: any, idx: number) => (
                                        <li key={idx}>{dat.project_name}</li>
                                    ))}
                            </ol>
                        </Grid>

                        {/* ----------------- Buttons ----------------- */}
                        <Grid item xs={12}>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    mt: 4,
                                    width: "100%",
                                }}
                            >
                                <Box style={{ marginRight: 8 }}>
                                    <StyledCancelButton onClick={close} isMobile={isMobile}>
                                        Cancel
                                    </StyledCancelButton>
                                </Box>
                                <Box>
                                    <StyledCancelButton
                                        onClick={deleteProjectFirebase}
                                        isMobile={isMobile}
                                    >
                                        {load ? (
                                            <span style={{ display: "flex", justifyContent: "center" }}>
                                                <Circles height={20} width={20} />Deleting...
                                            </span>
                                        ) : (
                                            <>Delete</>
                                        )}
                                    </StyledCancelButton>
                                </Box>
                            </Box>
                        </Grid>
                    </form>
                </Box>
            </Modal>
        </>
    );
};

// -------------------------------------------------------------
// Redux → pass theme into component
// -------------------------------------------------------------
const mapStateToProps = (state: any) => {
    return {
        theme: state.theme.isLightMode,
    };
};

export default connect(mapStateToProps)(DeleteMasterProjectSelectedModal);
